import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Shield, TrendingUp, AlertTriangle, CheckCircle, Target, ChevronRight, Loader2 } from "lucide-react";

function ReadinessRing({ score, size = 160 }) {
  const r = (size - 16) / 2, c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  const color = score >= 70 ? "#588157" : score >= 40 ? "#D4A373" : "#BC4749";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="hsl(20,5%,90%)" strokeWidth="10" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth="10" fill="none" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" className="score-ring" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-bold" style={{ color }}>{score}%</span>
        <span className="font-body text-xs text-muted-foreground">Übungsstand</span>
      </div>
    </div>
  );
}

export default function ReadinessPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/readiness").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const levelColors = { fortgeschritten: "text-success", aufbauend: "text-warning", einstieg: "text-primary", keine_daten: "text-muted-foreground" };

  return (
    <div className="page-transition max-w-3xl mx-auto space-y-6" data-testid="readiness-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Trainingsfortschritt</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Ihr aktueller Übungsstand basierend auf Ihrem Trainingsverlauf</p>
      </div>

      {/* Disclaimer */}
      <div className="bg-muted/50 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="font-body text-xs text-muted-foreground">
          <strong>Hinweis:</strong> Diese Werte spiegeln Ihren Übungsstand im Trainingssystem wider. Sie sind KEINE Prognose, KEIN Gutachten 
          und KEINE Aussage über Ihre tatsächliche MPU-Reife. Für eine fundierte Einschätzung wenden Sie sich an einen qualifizierten MPU-Berater.
        </p>
      </div>

      {/* Score */}
      <Card className="border-border/50" data-testid="readiness-score-card">
        <CardContent className="pt-8 pb-6 flex flex-col items-center">
          <ReadinessRing score={data.score} />
          <Badge className={`mt-4 text-sm font-body ${levelColors[data.level]}`} variant="secondary">{data.label}</Badge>
          <p className="font-body text-sm text-muted-foreground mt-3 text-center max-w-md">{data.recommendation}</p>
        </CardContent>
      </Card>

      {/* Factors */}
      {data.factors && Object.keys(data.factors).length > 0 && (
        <Card className="border-border/50" data-testid="readiness-factors">
          <CardHeader><CardTitle className="font-heading text-lg">Bewertungsfaktoren</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Durchschnittsbewertung", value: `${data.factors.durchschnittliche_bewertung}/10`, icon: Target },
                { label: "Trend", value: data.factors.trend === "steigend" ? "Steigend" : data.factors.trend === "fallend" ? "Fallend" : "Stabil", icon: TrendingUp },
                { label: "Sessions abgeschlossen", value: data.factors.sessions_abgeschlossen, icon: CheckCircle },
                { label: "Themen abgedeckt", value: data.factors.themen_abgedeckt, icon: Target },
                { label: "Reflexionen", value: data.factors.reflexionen_geschrieben, icon: CheckCircle },
                { label: "Rote Flaggen", value: data.factors.rote_flaggen || 0, icon: AlertTriangle },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <f.icon className={`w-4 h-4 ${f.label === "Rote Flaggen" && f.value > 0 ? "text-error" : "text-primary"}`} />
                  <div>
                    <p className="font-body text-xs text-muted-foreground">{f.label}</p>
                    <p className="font-body text-sm font-medium">{f.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button className="flex-1 font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2" onClick={() => navigate("/exam")} data-testid="start-exam-btn">
          Prüfungssimulation starten <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" className="flex-1 font-body gap-2" onClick={() => navigate("/story")} data-testid="start-story-btn">
          Geschichte aufarbeiten <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
